"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import {
  House,
  X,
  BriefcaseBusiness,
  Building2,
  MapPin,
  MoveRight,
  MoveLeft,
  ArrowLeft,
} from "lucide-react";
import useCommonStore from "@/store/commonStore";
import Loader from "./loader";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { toast } from "@/hooks/use-toast";
import { zodResolver } from "@hookform/resolvers/zod";
import { addAddress, addressDetails, updateAddress } from "@/lib/cart";

export function AddAddress({
  translations,
  open,
  onClose,
  addressId,
  onBack,
  selectedAddress,
}: {
  translations: any;
  open: boolean;
  onClose: () => void;
  addressId?: string;
  onBack?: () => void;
  selectedAddress?: Record<string, any>;
}) {
  const [show, setShow] = useState(open);
  const modalRef = useRef<HTMLDivElement>(null);
  const [loading, setLoading] = useState(false);
  const [selectedType, setSelectedType] = useState("");

  interface Country {
    cntry_nm: string;
    cntry_area_cd: string;
    flag: string;
  }

  const [filteredCountries, setFilteredCountries] = useState<Country[]>([]);

  const { countries, user, fetchCountries, fetchAddressList } =
    useCommonStore();

  const defaultValues = {
    full_nm: user?.full_nm || "",
    phn_num: user?.phn_num || "",
    phn_cd: user?.phn_cd || "",
    email: user?.email || "",
    locality: selectedAddress?.locality || "",
    city: selectedAddress?.city || "",
    state: selectedAddress?.state || "",
    country: selectedAddress?.country || "",
    zip_code: selectedAddress?.zip_code || "",
    address_name: selectedAddress?.address_name || "",
    address_line1: selectedAddress?.address_line1 || "",
    address_line2: selectedAddress?.address_line2 || "",
    is_default: true,
    lat: selectedAddress?.lat || 0,
    lng: selectedAddress?.lng || 0,
    loc_str: selectedAddress?.loc_str || "",
  };

  const validationSchema = z.object({
    full_nm: z.string().nonempty("Full name is required"),
    phn_num: z.string().nonempty("Phone number is required"),
    phn_cd: z.string().nonempty("Country code is required"),
    email: z.string().email("Invalid email").or(z.literal("")).optional(),
    locality: z.string().nonempty("Locality is required"),
    city: z.string().nonempty("City is required"),
    state: z.string().nonempty("State is required"),
    country: z.string().nonempty("Country is required"),
    zip_code: z.string().optional().nullable(),
    address_name: z.string().nonempty("Address name is required"),
    address_line1: z.string().nonempty("House/Building name required"),
    address_line2: z.string().optional(),
    is_default: z.boolean().optional(),
    lat: z.number().optional(),
    lng: z.number().optional(),
    loc_str: z.string().optional(),
  });

  type FormType = z.infer<typeof validationSchema>;

  // ✅ FIX: Add `reValidateMode` for better validation triggers
  const {
    register,
    control,
    handleSubmit,
    formState: { errors },
    setValue,
    reset,
    trigger,
  } = useForm<FormType>({
    defaultValues,
    resolver: zodResolver(validationSchema),
    mode: "onSubmit",
    reValidateMode: "onChange",
  });
  useEffect(() => {
    reset(defaultValues);
  }, [selectedAddress]);

  // ✅ Load countries and address details properly
  useEffect(() => {
    if (open) {
      setShow(true);
      if (addressId && !selectedAddress) loadDetails(addressId);
      if (!countries?.length) loadCountries();
    }
  }, [open]);

  const loadDetails = async (address_id: string) => {
    setLoading(true);
    const result = await addressDetails({ address_id });
    setLoading(false);
    if (result?.status) {
      reset(result.data);
    } else {
      toast({
        title: "Error",
        description: result?.error || result?.message,
        variant: "destructive",
      });
    }
  };

  const loadCountries = async () => {
    setLoading(true);
    const result = await fetchCountries();
    setLoading(false);
    if (!result?.status) {
      toast({
        title: "Error",
        description: result?.error || result?.message,
        variant: "destructive",
      });
    }
  };

  if (!show) return null;

  const onSubmit = async (data: FormType) => {
    setLoading(true);
    let result;
    if (addressId) {
      result = await updateAddress({ ...data, address_id: addressId });
    } else {
      result = await addAddress(data);
    }
    setLoading(false);

    if (result?.status) {
      reset(defaultValues);
      onClose();
      await fetchAddressList();
      toast({
        title: "Success",
        description: result?.message,
        variant: "default",
      });
    } else {
      toast({
        title: "Error",
        description: result?.error || result?.message,
        variant: "destructive",
      });
    }
  };

  return (
    <div
      className={`fixed inset-0 bg-black/40 flex items-end md:items-center justify-center transition-opacity duration-300 z-[51] ${
        open ? "opacity-100" : "opacity-0 pointer-events-none"
      }`}
      style={{ scrollBehavior: "smooth" }}
    >
      <div
        ref={modalRef}
        className={`bg-white w-full max-w-full md:max-w-lg rounded-none md:rounded-2xl relative x transition-transform duration-300 overflow-hidden h-full md:max-h-[90dvh]
        ${
          open ? "translate-y-0 md:scale-100" : "translate-y-full md:scale-95"
        }`}
      >
        <div className="p-4 md:p-6 fixed top-0 bg-white z-10 shadow md:shadow-md left-0 right-0">
          <button
            className="absolute right-4 md:right-6 top-4 md:top-6 text-gray-400 hover:text-gray-600 rtl:left-4 rtl:right-auto"
            onClick={() => {
              reset(defaultValues);
              onClose();
            }}
          >
            <X className="w-6 h-6" />
          </button>

          <div className="font-bold text-lg flex items-center gap-2">
            <span onClick={onBack} className="">
              <ArrowLeft />
            </span>
            <span>
              {addressId
                ? `${translations?.["udt_add"] || "Update Address"}`
                : `${translations?.["add_address"] || "Add New Address"}`}
            </span>
          </div>
        </div>
        <div className="p-4 md:p-6 pt-20 md:pt-24 max-h-[calc(100vh-80px)] md:max-h-[calc(100vh-194px)] overflow-y-auto">
          {loading ? (
            <Loader />
          ) : (
            <form
              method="POST"
              onSubmit={handleSubmit(onSubmit)}
              className="relative"
            >
              <div className="">
                {/* Full Name */}
                <div className="mb-2">
                  <label
                    htmlFor="full_nm"
                    className="text-sm text-gray-400 font-semibold mb-2"
                  >
                    {translations?.["full_nm"] || "Full Name"}
                  </label>
                  <input
                    {...register("full_nm")}
                    autoComplete="off"
                    className="w-full border rounded-md px-2 py-3 placeholder:text-gray-300 text-lg"
                    placeholder={translations?.["full_nm"] || "Full Name"}
                  />
                  {errors.full_nm && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.full_nm.message}
                    </p>
                  )}
                </div>

                {/* Phone */}
                <div className="mb-2">
                  <label className="text-sm text-gray-400 font-semibold mb-2">
                    {translations?.["mbl_nm"] || "Mobile"}
                  </label>
                  <div className="flex gap-1">
                    <Controller
                      name="phn_cd"
                      control={control}
                      render={({ field }) => (
                        <Select
                          key={field.value}
                          value={field.value}
                          onValueChange={(value) => field.onChange(value)}
                        >
                          <SelectTrigger className="w-[140px] h-[50px] border rounded-md ">
                            <SelectValue placeholder="+" />
                          </SelectTrigger>
                          {/* Ensure SelectContent sits above the modal.
                            If your Select component supports a portal prop, enable it (e.g. portalled or portal). */}
                          <SelectContent
                            className="z-[99999] "
                            position="popper" /* portalled={true} */
                          >
                            {countries?.map((e, i) => (
                              <SelectItem
                                key={i}
                                value={
                                  e.cntry_area_cd?.toString().startsWith("+")
                                    ? e.cntry_area_cd
                                    : `+${e.cntry_area_cd}`
                                }
                              >
                                {e.cntry_area_cd?.toString().startsWith("+")
                                  ? e.cntry_area_cd
                                  : `+${e.cntry_area_cd}`}{" "}
                                {e.flag}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    <input
                      {...register("phn_num")}
                      autoComplete="off"
                      className="flex-1 border rounded-md px-2 py-3 placeholder:text-gray-300 text-lg"
                      placeholder={translations?.["mbl_nm"] || "Mobile"}
                    />
                  </div>
                  {(errors.phn_cd || errors.phn_num) && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.phn_cd?.message || errors.phn_num?.message}
                    </p>
                  )}
                </div>

                {/* Email */}
                <div className="mb-2">
                  <label className="text-sm text-gray-400 font-semibold mb-2">
                    {translations?.["email"] || "Email"}
                  </label>
                  <input
                    {...register("email")}
                    autoComplete="off"
                    className="w-full border rounded-md px-2 py-3 placeholder:text-gray-300 text-lg"
                    placeholder={translations?.["email"] || "Email"}
                  />
                  {errors.email && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.email.message}
                    </p>
                  )}
                </div>
                <div className="mb-2">
                  <label className="text-sm text-gray-400 font-semibold mb-2">
                    {translations?.["add_nm"] || "Address Name"}
                  </label>
                  <div className="flex gap-3 mb-2">
                    {["Home", "Work", "Flat", "Other"].map((type) => (
                      <button
                        key={type}
                        type="button"
                        onClick={() => {
                          setValue("address_name", type);
                          setSelectedType(type);
                          trigger("address_name"); // ✅ re-validate field
                        }}
                        className={`border rounded py-1 px-2 text-xs font-bold flex items-center gap-1 ${
                          type === selectedType ? "bg-primary text-white" : ""
                        }`}
                      >
                        {type === `${translations?.["home"] || "Home"}` && (
                          <>
                            <House className="w-4 h-4" />
                            {type}
                          </>
                        )}
                        {type === `${translations?.["wrk"] || "Work"}` && (
                          <>
                            <BriefcaseBusiness className="w-4 h-4" />
                            {type}
                          </>
                        )}
                        {type === `${translations?.["flat"] || "Flat"}` && (
                          <>
                            <Building2 className="w-4 h-4" />
                            {type}
                          </>
                        )}
                        {type === `${translations?.["other"] || "Other"}` && (
                          <>
                            <MapPin className="w-4 h-4" />
                            {type}
                          </>
                        )}
                      </button>
                    ))}
                  </div>
                  <input
                    {...register("address_name")}
                    autoComplete="off"
                    className="w-full border rounded-md px-2 py-3 placeholder:text-gray-300 text-lg"
                    placeholder={translations?.["add_nm"] || "Address Name"}
                  />
                  {errors.address_name && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.address_name.message}
                    </p>
                  )}
                </div>

                {/* Address lines */}
                <div className="mb-2">
                  <label className="text-sm text-gray-400 font-semibold mb-2">
                    {translations?.["hous_num_build"] ||
                      "HouseNo., Building Name"}
                  </label>
                  <input
                    {...register("address_line1")}
                    autoComplete="off"
                    className="w-full border rounded-md px-2 py-3 placeholder:text-gray-300 text-lg"
                    placeholder={
                      translations?.["hous_num_build"] ||
                      "House No., Building Name"
                    }
                  />
                  {errors.address_line1 && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.address_line1.message}
                    </p>
                  )}
                </div>
                <div className="mb-2">
                  <label className="text-sm text-gray-400 font-semibold mb-2">
                    {translations?.["rd_area_clny"] ||
                      "Road name, Area, Colony"}
                  </label>
                  <textarea
                    rows={2}
                    {...register("address_line2")}
                    autoComplete="off"
                    className="w-full border rounded-md px-2 py-3 placeholder:text-gray-300 resize-none"
                    placeholder={
                      translations?.["rd_area_clny"] ||
                      "Road name, Area, Colony"
                    }
                  />
                  {errors.address_line2 && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.address_line2.message}
                    </p>
                  )}
                </div>
                {/* Locality */}
                <div className="mb-2">
                  <label className="text-sm text-gray-400 font-semibold mb-2">
                    {translations?.["lclty"] || "Locality"}
                  </label>
                  <input
                    {...register("locality")}
                    autoComplete="off"
                    className="w-full border rounded-md px-2 py-3 placeholder:text-gray-300 text-lg"
                    placeholder={translations?.["lclty"] || "Locality"}
                  />
                  {errors.locality && (
                    <p className="text-red-500 text-xs mt-1">
                      {errors.locality.message}
                    </p>
                  )}
                </div>

                {/* Zip & City */}
                <div className="flex gap-2 mb-2">
                  <div className="flex-1">
                    <label className="text-sm text-gray-400 font-semibold mb-2">
                      {translations?.["city"] || "City"}
                    </label>
                    <input
                      {...register("city")}
                      autoComplete="off"
                      className="w-full border rounded-md px-2 py-3 placeholder:text-gray-300 text-lg"
                      placeholder={translations?.["city"] || "City"}
                    />
                    {errors.city && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.city.message}
                      </p>
                    )}
                  </div>
                  <div className="flex-1">
                    <label className="text-sm text-gray-400 font-semibold mb-2">
                      {translations?.["stat_prov"] || "State / Province"}
                    </label>
                    <input
                      {...register("state")}
                      autoComplete="off"
                      className="w-full border rounded-md px-2 py-3 placeholder:text-gray-300"
                      placeholder={
                        translations?.["stat_prov"] || "State / Province"
                      }
                    />
                    {errors.state && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.state.message}
                      </p>
                    )}
                  </div>
                </div>

                {/* State & Country */}
                <div className="flex gap-2">
                  <div className="flex-1">
                    <label className="text-sm text-gray-400 font-semibold mb-2">
                      {translations?.["zip_cd"] || "Zip Code"}
                    </label>
                    <input
                      {...register("zip_code")}
                      autoComplete="off"
                      className="w-full border rounded-md px-2 py-3 placeholder:text-gray-300 text-lg"
                      placeholder="000000"
                    />
                    {errors.zip_code && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.zip_code.message}
                      </p>
                    )}
                  </div>

                  <div className="flex-1">
                    <label className="text-sm text-gray-400 font-semibold mb-2">
                      {translations?.["cntry"] || "Country"}
                    </label>
                    <Controller
                      name="country"
                      control={control}
                      render={({ field }) => (
                        <Select
                          key={field.value}
                          value={field.value}
                          onValueChange={(value) => field.onChange(value)}
                        >
                          <SelectTrigger className="border rounded-md h-[50px] w-full text-lg">
                            <SelectValue
                              placeholder={
                                translations?.["slct_cntry"] || "Select Country"
                              }
                            />
                          </SelectTrigger>
                          <SelectContent
                            className="z-[99999]"
                            position="popper" /* portalled={true} */
                          >
                            {countries?.map((e, i) => (
                              <SelectItem key={i} value={e.cntry_cd}>
                                {e.cntry_nm}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      )}
                    />
                    {errors.country && (
                      <p className="text-red-500 text-xs mt-1">
                        {errors.country.message}
                      </p>
                    )}
                  </div>
                </div>
              </div>

              <div className="fixed bottom-0 left-0 right-0 p-4 md:p-6 shadow-xl bg-white">
              <button
                type="submit"
                className="w-full bg-primary text-white p-3 rounded-md font-semibold"
              >
                {addressId
                  ? `${translations?.["udt_add"] || "Update Address"}`
                  : `${translations?.["add_address"] || "Add New Address"}`}
              </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
